import React from "react";
import { TrendingUp, TrendingDown, Scale } from "lucide-react";
import { formatCurrency, COMMITTEE } from "../utils/mockData";

export default function Finance({ donations = [], expenses = [] }) {
  const totalDonations = donations.reduce((s, d) => s + (d.amount || 0), 0);
  const totalExpenses = expenses.reduce((s, e) => s + (e.amount || 0), 0);
  const netBalance = totalDonations - totalExpenses;
  const utilizationPct = totalDonations > 0 ? ((totalExpenses / totalDonations) * 100).toFixed(1) : 0;

  const paymentModeData = [
    { name: "Cash", value: donations.filter((d) => d.paymentMode === "Cash").reduce((s, d) => s + (d.amount || 0), 0), color: "#22c55e" },
    { name: "UPI", value: donations.filter((d) => d.paymentMode === "UPI").reduce((s, d) => s + (d.amount || 0), 0), color: "#3b82f6" },
    { name: "NEFT", value: donations.filter((d) => d.paymentMode === "NEFT").reduce((s, d) => s + (d.amount || 0), 0), color: "#8b5cf6" },
    { name: "Cheque", value: donations.filter((d) => d.paymentMode === "Cheque").reduce((s, d) => s + (d.amount || 0), 0), color: "#f59e0b" },
  ];

  const summaryCards = [
    { label: "Total Income", value: formatCurrency(totalDonations), icon: TrendingUp, color: "text-green-600", bg: "bg-green-50 border-green-100" },
    { label: "Total Expenditure", value: formatCurrency(totalExpenses), icon: TrendingDown, color: "text-red-600", bg: "bg-red-50 border-red-100" },
    { label: "Net Balance", value: formatCurrency(netBalance), icon: Scale, color: netBalance >= 0 ? "text-blue-600" : "text-red-600", bg: "bg-blue-50 border-blue-100" },
  ];

  return (
    <div className="p-4 lg:p-6 space-y-6 fade-in">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Finance Overview</h1>
        <p className="text-sm text-gray-500">Ganesh Chaturthi 2026 — {COMMITTEE.name}</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {summaryCards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.label} className={`bg-white rounded-2xl p-4 border shadow-sm ${card.bg}`}>
              <Icon size={18} className={`${card.color} mb-2`} />
              <p className={`text-xl font-black ${card.color}`}>{card.value}</p>
              <p className="text-xs text-gray-500 font-medium mt-0.5">{card.label}</p>
            </div>
          );
        })}
      </div>

      {/* Budget Utilization */}
      <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold text-gray-900">Budget Utilization</h2>
          <span className={`text-sm font-bold px-3 py-1 rounded-full ${
            Number(utilizationPct) > 90 ? "bg-red-100 text-red-700" :
            Number(utilizationPct) > 70 ? "bg-yellow-100 text-yellow-700" :
            "bg-green-100 text-green-700"
          }`}>
            {utilizationPct}% utilized
          </span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
          <div
            className={`h-3 rounded-full transition-all duration-700 ${
              Number(utilizationPct) > 90 ? "bg-red-500" :
              Number(utilizationPct) > 70 ? "bg-yellow-500" : "bg-green-500"
            }`}
            style={{ width: `${Math.min(utilizationPct, 100)}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-400">
          <span>₹0</span>
          <span>Budget: {formatCurrency(totalDonations)}</span>
        </div>
      </div>

      {/* Payment Mode Distribution */}
      <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
        <h2 className="text-sm font-bold text-gray-900 mb-4">Payment Mode Distribution</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {paymentModeData.map((pm) => {
            const pct = totalDonations > 0 ? ((pm.value / totalDonations) * 100).toFixed(1) : 0;
            return (
              <div key={pm.name} className="text-center p-3 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center"
                  style={{ backgroundColor: `${pm.color}20` }}>
                  <div className="w-6 h-6 rounded-full" style={{ backgroundColor: pm.color }} />
                </div>
                <p className="text-lg font-black" style={{ color: pm.color }}>{pct}%</p>
                <p className="text-xs font-semibold text-gray-600">{pm.name}</p>
                <p className="text-[10px] text-gray-400">{formatCurrency(pm.value)}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* P&L Statement */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-5 text-white">
        <h2 className="text-sm font-bold text-gray-300 mb-4 uppercase tracking-wider">Profit & Loss Statement</h2>
        <div className="space-y-3">
          <div className="flex justify-between items-center py-2 border-b border-gray-700">
            <span className="text-gray-300 text-sm">Total Income (Donations)</span>
            <span className="text-green-400 font-bold">{formatCurrency(totalDonations)}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-gray-700">
            <span className="text-gray-300 text-sm">Total Expenditure</span>
            <span className="text-red-400 font-bold">({formatCurrency(totalExpenses)})</span>
          </div>
          <div className="flex justify-between items-center py-2">
            <span className="text-white font-bold text-base">Net Balance</span>
            <span className={`text-xl font-black ${netBalance >= 0 ? "text-green-400" : "text-red-400"}`}>
              {formatCurrency(Math.abs(netBalance))}
              {netBalance >= 0 ? " ✓" : " ✗"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
